import { FC, useCallback } from 'react'
import { useTranslation } from 'next-i18next'

import SpaceAddressMapComponent from 'components/maps/SpaceAddressMapComponent'

import type { Space } from 'features/space/types/spaceDetail'

import { IntroductionBlock } from './IntroductionBlock'
import space_styles from './SpaceInformationSection.module.scss'

type Props = {
  space: Space
  showHeading?: boolean
  showPhone?: boolean
  showStation?: boolean
  showAddress?: boolean
  showMap?: boolean
  showIntroduction?: boolean
}

/** スペース情報セクション (電話番号・最寄り駅・住所・地図・紹介文)。 */
export const SpaceInformationSection: FC<Props> = ({
  space,
  showHeading = true,
  showPhone = true,
  showStation = true,
  showAddress = true,
  showMap = true,
  showIntroduction = true,
}) => {
  const { t } = useTranslation()

  const nearestStation = space.nearby_stations[0]

  const handleViewOnGoogleMap = useCallback(() => {
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${space.latitude},${space.longitude}`
    )
  }, [space.latitude, space.longitude])

  const hasContactContent =
    showPhone || (showStation && nearestStation) || showAddress || showMap || showIntroduction

  if (!hasContactContent) return null

  return (
    <section className={space_styles.spaceInformationSection}>
      {showHeading && (
        <div className={space_styles.spaceInformationSectionHeading}>
          <h2 className={space_styles.spaceInformationSectionHeadingTitle}>
            {t('SPACE_DETAIL.SECTION.SPACE_INFORMATION.TITLE')}
          </h2>
        </div>
      )}
      {showPhone && (
        <div className={space_styles.sectionContents}>
          <div className={space_styles.phoneNumberSection}>
            <div className={space_styles.phoneNumberSectionLabel}>
              <img src="/phone_number_icon.svg" alt="" height="24px" width="24px" />
              <p className={space_styles.labelName}>
                {t('SPACE_DETAIL.SECTION.SPACE_INFORMATION.PHONE_NUMBER')}
              </p>
            </div>
            <div className={space_styles.phoneNumberSectionDetail}>
              <p className={space_styles.phoneNumber}>
                {space.phone_number ? (
                  <a href={`tel:${space.phone_number}`}>{space.phone_number}</a>
                ) : (
                  '-'
                )}
              </p>
            </div>
          </div>
        </div>
      )}
      {showStation && nearestStation && (
        <div className={space_styles.sectionContents}>
          <div className={space_styles.nearestStationSection}>
            <div className={space_styles.nearestStationSectionLabel}>
              <img src="/nearest_station_icon.svg" alt="" height="24px" width="24px" />
              <p className={space_styles.labelName}>
                {t('SPACE_DETAIL.SECTION.SPACE_INFORMATION.NEAREST_STATION')}
              </p>
            </div>
            <div className={space_styles.nearestStationSectionDetail}>
              <p className={space_styles.nearestStation}>
                {t('SPACE_DETAIL.SECTION.BASIC_INFORMATION.WALKING_DISTANCE', {
                  detailed_place: nearestStation.station_name,
                  distance_from_station: nearestStation.distance,
                })}
              </p>
            </div>
          </div>
        </div>
      )}
      {showAddress && (
        <div className={space_styles.sectionContents}>
          <div className={space_styles.adressSection}>
            <div className={space_styles.adressSectionLabel}>
              <img src="/adress_icon.svg" alt="" height="24px" width="24px" />
              <p className={space_styles.labelName}>
                {t('SPACE_DETAIL.SECTION.SPACE_INFORMATION.ADRESS')}
              </p>
            </div>
            <div className={space_styles.adressSectionDetail}>
              <p onClick={handleViewOnGoogleMap} className={space_styles.adress}>
                {space.localed_address}
              </p>
            </div>
          </div>
        </div>
      )}
      {showMap && (
        <div className={space_styles.sectionContents}>
          <SpaceAddressMapComponent lat={space.latitude} lng={space.longitude} />
        </div>
      )}
      {showIntroduction && <IntroductionBlock space={space} />}
    </section>
  )
}
